import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionCortesGet, apiCortesInstance, authHeaderCortes } from "../../redux/actions/cortes/cortes";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider, InputNumber, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const diferenciaOf = (c) => {
  if (!c.cerrado) return null;
  const real = parseFloat(c.efectivo_real || 0) + parseFloat(c.tarjeta_real || 0) + parseFloat(c.transferencia_real || 0);
  const esperado = parseFloat(c.efectivo_esperado || 0) + parseFloat(c.tarjeta_esperado || 0) + parseFloat(c.transferencia_esperado || 0);
  return real - esperado;
};

export default function CortesPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.cortes);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const [abrirOpen, setAbrirOpen] = useState(false);
  const [fondoInicial, setFondoInicial] = useState(null);
  const [abriendo, setAbriendo] = useState(false);

  useEffect(() => { dispatch(actionCortesGet()); }, [dispatch]);

  const baseSearched = useMemo(() => {
    if (!search) return items;
    return items.filter((c) => String(c.id_corte).includes(search) || (c.cajero_nombre || "").toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  const counts = useMemo(() => ({
    total: baseSearched.length,
    abiertos: baseSearched.filter((c) => !c.cerrado).length,
    conDiferencia: baseSearched.filter((c) => c.cerrado && Math.abs(diferenciaOf(c) || 0) > 0.01).length,
  }), [baseSearched]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "abiertos") return baseSearched.filter((c) => !c.cerrado);
    if (activeFilter === "con_diferencia") return baseSearched.filter((c) => c.cerrado && Math.abs(diferenciaOf(c) || 0) > 0.01);
    return baseSearched;
  }, [baseSearched, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de cortes", icon: <i className="fa-solid fa-vault" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "abiertos", label: "Abiertos", icon: <i className="fa-solid fa-lock-open" />, iconCls: "paq-stat-icon-decoracion", count: counts.abiertos },
    { key: "con_diferencia", label: "Con diferencia", icon: <i className="fa-solid fa-triangle-exclamation" />, iconCls: "paq-stat-icon-barra", count: counts.conDiferencia },
  ];

  const handleAbrir = async () => {
    if (fondoInicial == null) return toast.warning("Ingresa el fondo inicial");
    setAbriendo(true);
    try {
      const { data } = await apiCortesInstance.post("/cortes/abrir", { fondo_inicial: fondoInicial }, { headers: authHeaderCortes() });
      toast.success("Corte abierto");
      setAbrirOpen(false);
      setFondoInicial(null);
      navigate(`/app/cortes/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir el corte");
    } finally {
      setAbriendo(false);
    }
  };

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Cortes de caja</Title>
                <Text className="paq-subtitle">Historial de cortes de caja abiertos y cerrados.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por folio o cajero..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Cortes ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} cortes encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => setAbrirOpen(true)}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Abrir corte
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin cortes que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((c) => {
                        const dif = diferenciaOf(c);
                        return (
                          <div key={c.id_corte} className="paq-card">
                            <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                              <i className="fa-solid fa-vault paq-card-banner-icon" />
                            </div>

                            <div className="paq-card-header">
                              <div className="paq-card-head">
                                <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>Corte #{c.id_corte}</Paragraph>
                                <span className="paq-tipo-badge paq-tipo-sonido">{c.fecha}</span>
                              </div>
                            </div>

                            <div className="paq-card-body">
                              <div className="paq-contenidos-box">
                                <i className="fa-solid fa-user paq-info-icon" />
                                <div className="paq-info-text">
                                  <span className="paq-info-label">Cajero</span>
                                  <span className="paq-info-value">{c.cajero_nombre}</span>
                                </div>
                              </div>
                              {c.cerrado === 1 && (
                                <div className="paq-contenidos-box">
                                  <i className="fa-solid fa-scale-balanced paq-info-icon paq-info-icon-receta" />
                                  <div className="paq-info-text">
                                    <span className="paq-info-label paq-info-label-receta">Diferencia</span>
                                    <span className="paq-info-value" style={{ color: Math.abs(dif) > 0.01 ? "#b91c1c" : "#15803d" }}>{money(dif)}</span>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="paq-card-footer">
                              <div className={`paq-bottom-strip ${!c.cerrado ? "is-green" : Math.abs(dif) > 0.01 ? "is-red" : "is-green"}`}>
                                <span className="paq-strip-label">{!c.cerrado ? "Abierto" : Math.abs(dif) > 0.01 ? "Con diferencia" : "Cuadrado"}</span>
                              </div>
                              <Button
                                className="paq-btn-details"
                                icon={<i className="fa-solid fa-circle-info" />}
                                onClick={() => navigate(`/app/cortes/${c.id_corte}`)}
                              >
                                Ver detalles
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Modal
        open={abrirOpen}
        onCancel={() => setAbrirOpen(false)}
        onOk={handleAbrir}
        okText="Abrir corte"
        cancelText="Cancelar"
        title="Abrir nuevo corte de caja"
        confirmLoading={abriendo}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <InputNumber style={{ width: "100%" }} min={0} precision={2} placeholder="Fondo inicial" value={fondoInicial} onChange={setFondoInicial} autoFocus />
      </Modal>
    </ConfigProvider>
  );
}
